import KeyboardEvent from '../../event/js/keyboard';

import * as globals from './globals';

function getPositionX(event) {
  // check if touch event, otherwise mouse event
  if (event.changedTouches && !!event.changedTouches.length) {
    return event.changedTouches[0].pageX;
  }

  return event.pageX;
}

class DynamicTable {
  constructor(tableElement) {
    this.tableElement = tableElement;
    this.navigationElement = tableElement.querySelector(globals.NAVIGATION_SELECTOR);
    this.prevButtonElement = tableElement.querySelector(globals.NAVIGATION_PREV_BUTTON_SELECTOR);
    this.nextButtonElement = tableElement.querySelector(globals.NAVIGATION_NEXT_BUTTON_SELECTOR);
    this.tableHeaderTitleElement = tableElement.querySelector(globals.HEADER_TITLE_SELECTOR);
    this.tableContentElement = tableElement.querySelector(globals.CONTENT_SELECTOR);
    this.rowElements = tableElement.querySelectorAll(globals.ROW_SELECTOR);
    this.scrollableContentElements = tableElement.querySelectorAll(globals.SCROLLABLE_CONTENT_SELECTOR);
    this.scrollableHeaderElement = tableElement.querySelector(globals.SCROLLABLE_HEADER_SELECTOR);
    this.scrollableContentElement = this.scrollableContentElements[0];

    this.isDownEvent = false;
    this.isMoveEvent = false;
    this.savedPositionX = 0;
    this.scrollLeft = 0;

    this.calculateWidth();
    window.addEventListener('resize', () => this.calculateWidth());

    this.prevButtonElement.addEventListener('click', () => this.scrollContent(globals.DIRECTION.PREV));
    this.nextButtonElement.addEventListener('click', () => this.scrollContent(globals.DIRECTION.NEXT));

    this.tableContentElement.addEventListener('mouseup', (event) => this.handleUpEvent(event));
    this.tableContentElement.addEventListener('mousedown', (event) => this.handleDownEvent(event));
    this.tableContentElement.addEventListener('mousemove', (event) => this.handleMoveEvent(event));

    this.scrollableContentElements.forEach((element) => {
      element.addEventListener('touchend', (event) => this.handleUpEvent(event));
      element.addEventListener('touchstart', (event) => this.handleDownEvent(event));
      element.addEventListener('touchmove', (event) => this.handleMoveEvent(event));
    });

    this.tableContentElement.addEventListener('keydown', (userEvent) => this.handleKeyPressed(userEvent));

    this.headerSpans = this.tableHeaderTitleElement ? Array.from(this.tableHeaderTitleElement.querySelectorAll('span')).slice(1) : [];

    this.ScrollableContent = null;
    this.firstRow = tableElement.querySelector(`${globals.CONTENT_SELECTOR} ${globals.ROW_SELECTOR}`);
    if (this.firstRow) {
      this.ScrollableContent = this.firstRow.querySelector(globals.SCROLLABLE_CONTENT_SELECTOR);
    }

    this.debouncedUpdate = this.debounce(this.updateNavAria, 50);

    if (this.ScrollableContent) {
      this.ScrollableContent.addEventListener('scroll', this.debouncedUpdate, { passive: true });
    }

    if (this.prevButtonElement) this.prevButtonElement.addEventListener('click', () => setTimeout(() => this.updateNavAria(), 120));
    if (this.nextButtonElement) this.nextButtonElement.addEventListener('click', () => setTimeout(() => this.updateNavAria(), 120));

    setTimeout(() => this.updateNavAria(), 150);
  }

  debounce(fn, wait) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  static findVisibleCellIndex(cells, containerRect) {
    const centerX = containerRect.left + containerRect.width / 2;

    for (let i = 0; i < cells.length; i += 1) {
      const r = cells[i].getBoundingClientRect();
      if (r.left <= centerX && r.right >= centerX) {
        return i;
      }
      // fallback: choose first cell whose left is >= container left
      if (r.left >= containerRect.left) {
        return i;
      }
    }

    return 0;
  }

  getHeaderText(index) {
    if (index >= 0 && index < this.headerSpans.length && this.headerSpans[index]) {
      return this.headerSpans[index].textContent.trim();
    }
    return '';
  }

  static setButtonAriaLabel(buttonElement, direction, headerText) {
    if (!buttonElement) return;

    const label = headerText
      ? `${direction} column ${headerText}`
      : `${direction} column`;

    buttonElement.setAttribute('aria-label', label);
  }

  updateNavAria() {
    if (!this.ScrollableContent || this.headerSpans.length === 0) return;

    const containerRect = this.ScrollableContent.getBoundingClientRect();
    const cells = Array.from(this.ScrollableContent.children);
    if (cells.length === 0) return;

    const visibleIndex = DynamicTable.findVisibleCellIndex(cells, containerRect);

    const prevHeader = this.getHeaderText(visibleIndex - 1);
    const nextHeader = this.getHeaderText(visibleIndex + 1);

    DynamicTable.setButtonAriaLabel(this.prevButtonElement, 'Previous', prevHeader);
    DynamicTable.setButtonAriaLabel(this.nextButtonElement, 'Next', nextHeader);
  }

  handleKeyPressed(userEvent) {
    if (!this.showNavigation) {
      return;
    }

    switch (true) {
      case KeyboardEvent.CheckKey(userEvent, KeyboardEvent.Key.LEFT):
        userEvent.preventDefault();
        this.scrollContent(globals.DIRECTION.PREV);
        break;
      case KeyboardEvent.CheckKey(userEvent, KeyboardEvent.Key.RIGHT):
        userEvent.preventDefault();
        this.scrollContent(globals.DIRECTION.NEXT);
        break;
      default:
        break;
    }
  }

  handleUpEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = false;
    this.isMoveEvent = false;

    if (this.savedPositionX - getPositionX(event) < -globals.SWIPE_THRESHOLD) {
      this.scrollContent(globals.DIRECTION.PREV);
    }

    if (this.savedPositionX - getPositionX(event) > globals.SWIPE_THRESHOLD) {
      this.scrollContent(globals.DIRECTION.NEXT);
    }
  }

  handleDownEvent(event) {
    if (!event.cancelable) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = true;
    this.savedPositionX = getPositionX(event);
  }

  handleMoveEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isMoveEvent = true;
  }

  updateNavigation() {
    if (!this.showNavigation) {
      this.scrollLeft = 0;
      this.scrollableHeaderElement.classList.remove(globals.SHADOW_EARLIER_CLASS);
      this.tableHeaderTitleElement.classList.remove(globals.SHADOW_LATER_CLASS);
      this.scrollableContentElements.forEach((element) => element.classList.remove(globals.SHADOW_EARLIER_CLASS));
      this.rowElements.forEach((rowElement) => rowElement.classList.remove(globals.SHADOW_LATER_CLASS));
      return;
    }

    if (this.scrollLeft <= 0) {
      this.prevButtonElement.setAttribute('disabled', true);
      this.scrollableHeaderElement.classList.remove(globals.SHADOW_EARLIER_CLASS);
      this.scrollableContentElements.forEach((element) => element.classList.remove(globals.SHADOW_EARLIER_CLASS));
    } else {
      this.prevButtonElement.removeAttribute('disabled');
      this.scrollableHeaderElement.classList.add(globals.SHADOW_EARLIER_CLASS);
      this.scrollableContentElements.forEach((element) => element.classList.add(globals.SHADOW_EARLIER_CLASS));
    }

    if (this.scrollLeft >= this.scrollableContentElement.scrollWidth - this.scrollableContentElement.clientWidth) {
      this.nextButtonElement.setAttribute('disabled', true);
      this.tableHeaderTitleElement.classList.remove(globals.SHADOW_LATER_CLASS);
      this.rowElements.forEach((rowElement) => rowElement.classList.remove(globals.SHADOW_LATER_CLASS));
    } else {
      this.nextButtonElement.removeAttribute('disabled');
      this.tableHeaderTitleElement.classList.add(globals.SHADOW_LATER_CLASS);
      this.rowElements.forEach((rowElement) => rowElement.classList.add(globals.SHADOW_LATER_CLASS));
    }
  }

  calculateWidth() {
    const screenWidth = window.innerWidth;
    const columnsCount = this.scrollableContentElement.children.length + 1;
    let template;

    if (screenWidth < globals.SCREENSIZE_SM) template = '50% 50%';
    else if (columnsCount === 2) template = '55% 45%';
    else if (columnsCount === 3 || columnsCount === 4) template = '40% 60%';
    this.tableElement.style = `--at-dynamic-table--columns-template: ${template}`;

    if (screenWidth >= globals.SCREENSIZE_SM || columnsCount <= 2) {
      this.showNavigation = false;
      this.navigationElement.style.display = 'none';
      this.tableHeaderTitleElement.classList.add('at-border-radius-top-left--rounded', 'at-border-radius-top-right--rounded');
    } else if (columnsCount > 2) {
      this.showNavigation = true;
      this.navigationElement.style.display = 'flex';
      this.tableHeaderTitleElement.classList.remove('at-border-radius-top-left--rounded', 'at-border-radius-top-right--rounded');
    }

    this.updateNavigation();
  }

  scrollContent(direction) {
    let newScrollLeftValue = direction === globals.DIRECTION.NEXT
      ? this.scrollableContentElement.scrollWidth - this.scrollableContentElement.clientWidth
      : 0;

    if (direction === globals.DIRECTION.NEXT) {
      while (newScrollLeftValue - this.scrollableContentElement.clientWidth > this.scrollLeft) {
        newScrollLeftValue -= this.scrollableContentElement.clientWidth;
      }
    } else {
      while (newScrollLeftValue + this.scrollableContentElement.clientWidth < this.scrollLeft) {
        newScrollLeftValue += this.scrollableContentElement.clientWidth;
      }
    }

    this.scrollableHeaderElement.scroll({ left: newScrollLeftValue, behavior: 'smooth' });
    this.scrollableContentElements.forEach((element) => {
      element.scroll({ left: newScrollLeftValue, behavior: 'smooth' });
    });

    this.scrollLeft = newScrollLeftValue;

    this.updateNavigation();
  }
}

const initializeDynamicTable = () => {
  document.querySelectorAll(globals.DYNAMIC_TABLE_DATA_ELEMENT).forEach((dynamicTable) => new DynamicTable(dynamicTable));
};

export {
  DynamicTable,
  initializeDynamicTable,
};